import { api } from "@glasshalf/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Boxes, ClipboardList, FileText, Home, LogOut, PackageCheck, Plug, Settings, Shirt, Store, Truck, Users, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ConfirmImpactDialog } from "./components/ConfirmImpactDialog";
import { Drawer } from "./components/Drawer";
import { StatusBadge } from "./components/StatusBadge";
import { ArchivePage } from "./features/archive/ArchivePage";
import { BarcodePage } from "./features/barcode/BarcodePage";
import { ContentPage } from "./features/content/ContentPage";
import { CustomersPage } from "./features/customers/CustomersPage";
import { HomePage } from "./features/home/HomePage";
import { IntegrationsPage } from "./features/integrations/IntegrationsPage";
import { InventoryPage } from "./features/inventory/InventoryPage";
import { OrdersPage } from "./features/orders/OrdersPage";
import { BarcodeLabelsPage } from "./features/products/BarcodeLabelsPage";
import { ProductDetailPage } from "./features/products/ProductDetailPage";
import { ProductsPage } from "./features/products/ProductsPage";
import { ReceivingPage } from "./features/receiving/ReceivingPage";
import { ReportsPage } from "./features/reports/ReportsPage";
import { SettingsDrawerContent, SettingsPage } from "./features/settings/SettingsPage";
import { ShippingPage } from "./features/shipping/ShippingPage";
import { StoresPage } from "./features/stores/StoresPage";
import { formatDateTime, formatMoney, formatNumber } from "./lib/format";
import type { AnyRecord, DrawerState, OrderRow } from "./lib/types";

const navItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/products", label: "Products", icon: Shirt, sectionStart: true },
  { path: "/inventory", label: "Inventory", icon: Boxes },
  { path: "/receiving", label: "Receiving", icon: PackageCheck },
  { path: "/orders", label: "Orders", icon: ClipboardList },
  { path: "/shipping", label: "Shipping", icon: Truck },
  { path: "/stores", label: "Stores", icon: Store },
  { path: "/customers", label: "Customers", icon: Users, sectionStart: true },
  { path: "/content", label: "Content", icon: FileText },
  { path: "/reports", label: "Reports", icon: BarChart3 },
  { path: "/integrations", label: "Integrations", icon: Plug, sectionStart: true },
  { path: "/settings", label: "Settings", icon: Settings }
];

export function App() {
  const queryClient = useQueryClient();
  const statusQuery = useQuery({ queryKey: ["status"], queryFn: api.status });
  const status = statusQuery.data;

  if (statusQuery.isLoading) return <BootScreen title="연결 상태 확인 중" />;
  if (statusQuery.error) return <BootScreen title="서버 연결을 확인해주세요" body={(statusQuery.error as Error).message} />;
  if (!status?.hasAdmin) return <SetupScreen onDone={() => queryClient.invalidateQueries({ queryKey: ["status"] })} />;
  if (!status.authenticated) return <LoginScreen onDone={() => queryClient.invalidateQueries({ queryKey: ["status"] })} />;
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const bootstrapQuery = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const data = bootstrapQuery.data;
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    }
  });
  const appVersion = useQuery({
    queryKey: ["app-version"],
    queryFn: api.status,
    refetchInterval: 60_000
  });
  const hasUpdate = Boolean(data?.assetVersion && appVersion.data?.assetVersion && data.assetVersion !== appVersion.data.assetVersion);

  if (bootstrapQuery.isLoading) return <BootScreen title="운영 데이터를 불러오는 중" />;
  if (bootstrapQuery.error || !data) return <BootScreen title="서버 연결을 확인해주세요" body={(bootstrapQuery.error as Error)?.message || "초기 상태를 불러오지 못했습니다."} />;

  return (
    <div className={drawer ? "app-shell has-drawer" : "app-shell"}>
      <aside className="sidebar" aria-label="백오피스 주요 메뉴">
        <div className="brand">
          <strong>GLASSHALF</strong>
          <span>Backoffice</span>
        </div>
        <nav className="nav-list">
          {navItems.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
        </nav>
        <div className="sidebar-footer">
          {hasUpdate ? <button type="button" className="update-button" onClick={() => window.location.reload()}>업데이트</button> : null}
          <button type="button" className="logout-button" onClick={() => logout.mutate()} disabled={logout.isPending}>
            <LogOut size={15} />
            로그아웃
          </button>
        </div>
      </aside>
      <main className="workspace" id="workspaceMain">
        <header className="topbar">
          <div>
            <h1>{pageTitleFromPath(location.pathname)}</h1>
            <p>{pageDescriptionFromPath(location.pathname)}</p>
          </div>
          <div className="topbar-status">
            <span>{formatDateTime(new Date().toISOString())}</span>
          </div>
        </header>
        <Routes>
          <Route path="/" element={<HomePage data={data} />} />
          <Route path="/products" element={<ProductsPage data={data} />} />
          <Route path="/products/barcodes" element={<BarcodeLabelsPage data={data} />} />
          <Route path="/products/:productKey" element={<ProductDetailPage data={data} />} />
          <Route path="/inventory" element={<InventoryPage data={data} drawer={drawer} setDrawer={setDrawer} />} />
          <Route path="/receiving" element={<ReceivingPage data={data} />} />
          <Route path="/mobile/barcode" element={<BarcodePage data={data} />} />
          <Route path="/orders" element={<OrdersPage data={data} drawer={drawer} setDrawer={setDrawer} />} />
          <Route path="/shipping" element={<ShippingPage data={data} drawer={drawer} setDrawer={setDrawer} />} />
          <Route path="/stores" element={<StoresPage data={data} />} />
          <Route path="/customers" element={<CustomersPage data={data} />} />
          <Route path="/content" element={<ContentPage />} />
          <Route path="/reports" element={<ReportsPage data={data} />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/integrations" element={<IntegrationsPage data={data} drawer={drawer} setDrawer={setDrawer} />} />
          <Route path="/settings" element={<SettingsPage data={data} drawer={drawer} setDrawer={setDrawer} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <GlobalDrawer drawer={drawer} setDrawer={setDrawer} />
    </div>
  );
}

function NavItem({ path, label, icon: Icon, sectionStart }: { path: string; label: string; icon: LucideIcon; sectionStart?: boolean }) {
  const location = useLocation();
  const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
  return (
    <>
      {sectionStart ? <span className="nav-separator" aria-hidden="true" /> : null}
      <Link to={path} className={active ? "nav-item is-active" : "nav-item"}>
        <Icon size={16} />
        <span>{label}</span>
      </Link>
    </>
  );
}

function LoginScreen({ onDone }: { onDone: () => void }) {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({ mutationFn: () => api.login(loginId, password), onSuccess: onDone });
  return (
    <AuthCard title="로그인" body="GLASSHALF 운영 콘솔에 접근하려면 관리자 아이디와 비밀번호를 입력하세요.">
      <form onSubmit={(event) => { event.preventDefault(); login.mutate(); }}>
        <label>아이디<input value={loginId} onChange={(event) => setLoginId(event.target.value)} autoComplete="username" /></label>
        <label>비밀번호<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" /></label>
        {login.error ? <p className="form-error">{(login.error as Error).message}</p> : null}
        <button type="submit" className="primary" disabled={login.isPending}>{login.isPending ? "확인 중" : "로그인"}</button>
      </form>
    </AuthCard>
  );
}

function SetupScreen({ onDone }: { onDone: () => void }) {
  const [setupToken, setSetupToken] = useState("");
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const setup = useMutation({ mutationFn: () => api.setup({ setupToken, loginId, password }), onSuccess: onDone });
  return (
    <AuthCard title="첫 관리자 만들기" body="최초 1회만 관리자 계정을 만듭니다.">
      <form onSubmit={(event) => { event.preventDefault(); setup.mutate(); }}>
        <label>Setup Token<input value={setupToken} onChange={(event) => setSetupToken(event.target.value)} autoComplete="one-time-code" /></label>
        <label>관리자 아이디<input value={loginId} onChange={(event) => setLoginId(event.target.value)} autoComplete="username" /></label>
        <label>관리자 비밀번호<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" /></label>
        {setup.error ? <p className="form-error">{(setup.error as Error).message}</p> : null}
        <button type="submit" className="primary" disabled={setup.isPending}>{setup.isPending ? "생성 중" : "관리자 계정 만들기"}</button>
      </form>
    </AuthCard>
  );
}

function AuthCard({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <main className="auth-screen">
      <section className="auth-card">
        <span>GLASSHALF Backoffice</span>
        <h1>{title}</h1>
        <p>{body}</p>
        {children}
      </section>
    </main>
  );
}

function BootScreen({ title, body }: { title: string; body?: string }) {
  return (
    <main className="auth-screen">
      <section className="auth-card">
        <span>GLASSHALF Backoffice</span>
        <h1>{title}</h1>
        {body ? <p className="form-error">{body}</p> : <p>잠시만 기다려 주세요.</p>}
      </section>
    </main>
  );
}

function GlobalDrawer({ drawer, setDrawer }: { drawer: DrawerState; setDrawer: (drawer: DrawerState) => void }) {
  const queryClient = useQueryClient();
  const [orderAction, setOrderAction] = useState<"order_hold" | "cancel_request" | "return_exchange" | null>(null);
  const [orderReason, setOrderReason] = useState("");
  const orderCase = useMutation({
    mutationFn: () => {
      const order = drawer?.type === "order" ? drawer.record : null;
      return api.createOrderCase({
        caseType: orderAction,
        reason: orderReason,
        orderKey: order?.orderKey,
        orderIdMasked: order?.orderIdMasked,
        channelCode: order?.channelCode,
        customerLabel: order?.customerLabel,
        currentStatus: order?.statusBucket,
        items: order?.items || []
      });
    },
    onSuccess: async () => {
      setOrderAction(null);
      setOrderReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const content = useMemo(() => {
    if (!drawer) return null;
    if (drawer.type === "product") {
      return {
        title: drawer.record.productName,
        eyebrow: drawer.record.productKey,
        body: (
          <>
            <Section title="상품 요약">
              <KeyValue label="시즌" value={drawer.record.seasonCode} />
              <KeyValue label="카테고리" value={drawer.record.categoryName} />
              <KeyValue label="SKU" value={`${drawer.record.skuCount}개`} />
              <KeyValue label="온라인" value={drawer.record.channelStatus} />
            </Section>
            <Section title="SKU 매트릭스">
              {drawer.record.rows.slice(0, 12).map((row) => (
                <div className="drawer-row" key={row.sku}>
                  <span>{row.colorwayName || row.colorwayCode} / {row.sizeName || row.sizeCode}</span>
                  <strong>{row.readableAlias || row.sku}</strong>
                </div>
              ))}
            </Section>
            <Section title="위험 작업">
              <p className="muted">상품 삭제, Cafe24 적용 같은 외부 영향 작업은 별도 확인 모달에서 처리합니다.</p>
            </Section>
          </>
        )
      };
    }
    if (drawer.type === "sku") {
      const row = drawer.record;
      return {
        title: row.readableAlias || row.sku || "SKU",
        eyebrow: row.productName || "Inventory",
        body: (
          <>
            <Section title="재고 상태">
              <KeyValue label="실재고" value={row.onHandTotal ?? 0} />
              <KeyValue label="예약" value={row.reservedTotal ?? 0} />
              <KeyValue label="온라인 할당" value={row.allocatedTotal ?? 0} />
              <KeyValue label="가용" value={row.availableToSell ?? 0} />
            </Section>
            <Section title="위치별 수량">
              {Object.entries(row.balances || {}).map(([location, quantity]) => <KeyValue key={location} label={location} value={quantity} />)}
            </Section>
          </>
        )
      };
    }
    if (drawer.type === "order") {
      const order = drawer.record;
      return {
        title: order.orderIdMasked || order.orderKey || "주문",
        eyebrow: order.channelName || order.channelCode || "Orders",
        body: (
          <>
            <Section title="주문 상태">
              <KeyValue label="주문일" value={formatDateTime(order.orderedAt)} />
              <KeyValue label="채널" value={order.channelName || order.channelCode || "온라인몰"} />
              <KeyValue label="상태" value={<StatusBadge tone={orderDrawerStatusTone(order)}>{orderStatusLabelForDrawer(order.statusBucket)}</StatusBadge>} />
              <KeyValue label="금액" value={formatMoney(orderDrawerAmount(order))} />
              <KeyValue label="상품" value={orderDrawerItemSummary(order)} />
            </Section>
            <Section title="고객/배송">
              <KeyValue label="고객" value={order.customerLabel || "-"} />
              <KeyValue label="수령자" value={orderDrawerValue(order, ["receiverName", "recipientName", "shippingName", "buyerName"])} />
              <KeyValue label="연락처" value={orderDrawerValue(order, ["receiverPhoneMasked", "recipientPhoneMasked", "phoneMasked", "customerPhoneMasked"])} />
              <KeyValue label="주소" value={orderDrawerValue(order, ["addressSummary", "shippingAddressSummary", "receiverAddress", "shippingAddress"])} />
            </Section>
            <Section title="상품 라인">
              {orderDrawerItems(order).length ? orderDrawerItems(order).map((item, index) => (
                <div className="order-line-row" key={`${item.itemNo || item.sku || index}`}>
                  <div>
                    <strong>{item.productName || item.product_name || item.itemName || "상품"}</strong>
                    <span>{orderLineMeta(item)}</span>
                  </div>
                  <b>{formatNumber(Number(item.quantity ?? item.qty ?? item.orderQuantity ?? 0))}개</b>
                </div>
              )) : <p className="muted">상품 라인이 없습니다.</p>}
            </Section>
            <Section title="확인 포인트">
              <div className="order-risk-list">
                {orderDrawerRisks(order).map((risk) => (
                  <div className="order-risk-row" key={risk.title}>
                    <StatusBadge tone={risk.tone}>{risk.title}</StatusBadge>
                    <span>{risk.body}</span>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="운영 작업">
              <div className="drawer-action-list">
                <button type="button" onClick={() => setOrderAction("order_hold")}>출고 보류</button>
                <button type="button" onClick={() => setOrderAction("return_exchange")}>반품/교환 케이스</button>
                <button type="button" className="danger-outline" onClick={() => setOrderAction("cancel_request")}>취소 요청</button>
              </div>
              <p className="muted">외부 주문 상태를 바로 바꾸지 않고 케이스와 감사 로그를 먼저 남깁니다.</p>
            </Section>
          </>
        )
      };
    }
    if (drawer.type === "shipping") {
      const item = drawer.record;
      return {
        title: item.orderIdMasked || item.orderKey || "출고",
        eyebrow: item.channelName || "Shipping",
        body: (
          <>
            <Section title="출고 라인">
              <KeyValue label="상품" value={item.productName || "-"} />
              <KeyValue label="SKU" value={item.readableAlias || item.sku || "-"} />
              <KeyValue label="위치" value={item.locationName || item.locationCode || "-"} />
              <KeyValue label="수량" value={item.quantity || 0} />
            </Section>
            <Section title="출고 확정 영향">
              <p className="muted">출고 확정 때만 실제 오프라인 위치 재고가 차감됩니다. Cafe24 배송 상태 쓰기는 아직 자동 실행하지 않습니다.</p>
            </Section>
          </>
        )
      };
    }
    if (drawer.type === "settings") {
      return {
        title: String(drawer.record.name || drawer.record.label || drawer.record.code || drawer.record.action || "상세"),
        eyebrow: "Settings",
        body: <SettingsDrawerContent record={drawer.record} setDrawer={setDrawer} />
      };
    }
    return null;
  }, [drawer, setDrawer]);

  if (!drawer || !content) return null;
  return (
    <>
      <Drawer title={content.title} eyebrow={content.eyebrow} onClose={() => setDrawer(null)}>{content.body}</Drawer>
      <ConfirmImpactDialog
        open={drawer.type === "order" && Boolean(orderAction)}
        title={`${orderActionLabel(orderAction)} 케이스 생성`}
        currentValue={drawer.type === "order" ? orderStatusLabelForDrawer(drawer.record.statusBucket) : "-"}
        nextValue={orderActionLabel(orderAction)}
        externalImpact="Cafe24 주문 상태는 변경하지 않습니다. 내부 확인 케이스만 생성합니다."
        auditTrail="주문번호, 채널, 케이스 유형, 사유가 감사 로그에 남습니다."
        reason={orderReason}
        confirmLabel="케이스 생성"
        tone={orderAction === "cancel_request" ? "danger" : "default"}
        busy={orderCase.isPending}
        onReasonChange={setOrderReason}
        onCancel={() => {
          setOrderAction(null);
          setOrderReason("");
        }}
        onConfirm={() => orderCase.mutate()}
      />
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section className="drawer-section"><h3>{title}</h3>{children}</section>;
}

function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return <div className="key-value"><dt>{label}</dt><dd>{value}</dd></div>;
}

function orderDrawerItems(order: OrderRow): AnyRecord[] {
  return Array.isArray(order.items) ? order.items : [];
}

function orderDrawerAmount(order: OrderRow): number {
  return Number(order.totalAmount ?? order.paymentAmount ?? order.orderAmount ?? order.actualPaymentAmount ?? 0);
}

function orderDrawerQuantity(order: OrderRow): number {
  return orderDrawerItems(order).reduce((total, item) => total + Number(item.quantity ?? item.qty ?? item.orderQuantity ?? 0), 0);
}

function orderDrawerItemSummary(order: OrderRow): string {
  const lineCount = orderDrawerItems(order).length;
  const quantity = orderDrawerQuantity(order);
  if (!lineCount) return "0라인";
  return quantity ? `${formatNumber(lineCount)}라인 · ${formatNumber(quantity)}개` : `${formatNumber(lineCount)}라인`;
}

function orderDrawerValue(order: OrderRow, keys: string[]): ReactNode {
  for (const key of keys) {
    const value = order[key];
    if (value === undefined || value === null || value === "") continue;
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  }
  return "-";
}

function orderLineMeta(item: AnyRecord): string {
  return [
    item.readableAlias,
    item.sku,
    item.optionName || item.variantName || item.option_value || item.option,
    item.colorName && item.sizeName ? `${item.colorName} / ${item.sizeName}` : null
  ].filter(Boolean).join(" · ") || "-";
}

function orderDrawerRisks(order: OrderRow): Array<{ title: string; body: string; tone: "default" | "success" | "warning" | "danger" | "info" }> {
  const bucket = String(order.statusBucket || "").toLowerCase();
  const risks: Array<{ title: string; body: string; tone: "default" | "success" | "warning" | "danger" | "info" }> = [];
  if (!orderDrawerItems(order).length) {
    risks.push({ title: "상품 확인", body: "상품 라인이 비어 있어 수동 확인이 필요합니다.", tone: "warning" });
  }
  if (bucket.includes("cancel")) {
    risks.push({ title: "취소 확인", body: "취소 요청, 환불, 재고 복귀 여부를 확인합니다.", tone: "danger" });
  } else if (bucket.includes("return") || bucket.includes("exchange")) {
    risks.push({ title: "반품/교환", body: "회수 상태와 재입고 가능 여부를 확인합니다.", tone: "danger" });
  } else if (bucket.includes("check") || bucket.includes("hold") || bucket.includes("issue")) {
    risks.push({ title: "확인 필요", body: "주소, 결제, 품절, 출고 보류 사유를 확인합니다.", tone: "warning" });
  } else if (bucket.includes("ship") || bucket.includes("ready") || bucket.includes("pick") || bucket.includes("pack")) {
    risks.push({ title: "출고 확인", body: "Shipping에서 피킹, 패킹, 출고 확정으로 이어갑니다.", tone: "info" });
  } else {
    risks.push({ title: "주문 확인", body: "결제와 상품 라인을 확인한 뒤 출고 대상으로 넘깁니다.", tone: "default" });
  }
  return risks;
}

function orderDrawerStatusTone(order: OrderRow): "default" | "success" | "warning" | "danger" | "info" {
  const bucket = String(order.statusBucket || "").toLowerCase();
  if (bucket.includes("cancel") || bucket.includes("return") || bucket.includes("exchange")) return "danger";
  if (bucket.includes("check") || bucket.includes("hold") || bucket.includes("issue")) return "warning";
  if (bucket.includes("ship") || bucket.includes("ready") || bucket.includes("pick") || bucket.includes("pack")) return "info";
  return "default";
}

function orderActionLabel(action: "order_hold" | "cancel_request" | "return_exchange" | null): string {
  if (action === "order_hold") return "출고 보류";
  if (action === "cancel_request") return "취소 요청";
  if (action === "return_exchange") return "반품/교환 확인";
  return "주문 확인";
}

function orderStatusLabelForDrawer(status?: string): string {
  const bucket = String(status || "");
  if (!bucket) return "확인";
  return {
    ordered: "주문/결제",
    payment: "주문/결제",
    paid: "주문/결제",
    shipping: "배송/출고",
    cancelled: "취소",
    return_exchange: "반품/교환",
    needs_check: "확인 필요"
  }[bucket] || bucket;
}

function pageTitleFromPath(path: string): string {
  if (path.startsWith("/products/barcodes")) return "Barcode Labels";
  if (path.startsWith("/products")) return "Products";
  if (path.startsWith("/inventory")) return "Inventory";
  if (path.startsWith("/receiving")) return "Receiving";
  if (path.startsWith("/mobile/barcode")) return "Barcode";
  if (path.startsWith("/orders")) return "Orders";
  if (path.startsWith("/shipping")) return "Shipping";
  if (path.startsWith("/stores")) return "Stores";
  if (path.startsWith("/customers")) return "Customers";
  if (path.startsWith("/content")) return "Content";
  if (path.startsWith("/reports")) return "Reports";
  if (path.startsWith("/archive")) return "Archive";
  if (path.startsWith("/integrations")) return "Integrations";
  if (path.startsWith("/settings")) return "Settings";
  return "Home";
}

function pageDescriptionFromPath(path: string): string {
  if (path.startsWith("/products/barcodes")) return "상품 SKU 바코드 라벨을 확인하고 출력합니다.";
  if (path.startsWith("/products")) return "상품, 옵션, SKU, 온라인몰 매핑을 확인합니다.";
  if (path.startsWith("/inventory")) return "상품별 재고 현황을 보고 SKU별 위치, 조정, 온라인몰 할당을 관리합니다.";
  if (path.startsWith("/receiving")) return "입고 수량을 실제 오프라인 위치에 바로 반영합니다.";
  if (path.startsWith("/mobile/barcode")) return "모바일 카메라 또는 직접 입력으로 입고, 이동, 출고 작업을 처리합니다.";
  if (path.startsWith("/orders")) return "온라인몰 주문, 취소, 반품/교환 상태를 확인합니다.";
  if (path.startsWith("/shipping")) return "피킹, 패킹, 출고 확정을 처리합니다.";
  if (path.startsWith("/stores")) return "오프라인 매장 재고, 판매 차감, POS 동기화 상태를 확인합니다.";
  if (path.startsWith("/customers")) return "고객 응대가 필요한 주문과 케이스를 확인합니다.";
  if (path.startsWith("/content")) return "Archive, 룩북, 브랜드 페이지 같은 고객 노출 콘텐츠를 관리합니다.";
  if (path.startsWith("/reports")) return "운영 리스크와 감사 로그를 리포트 큐로 확인합니다.";
  if (path.startsWith("/archive")) return "Sanity 기반 Archive 콘텐츠와 Cafe24 반영 흐름을 관리합니다.";
  if (path.startsWith("/integrations")) return "온라인몰, 콘텐츠, 알림 같은 외부 서비스 연결을 관리합니다.";
  if (path.startsWith("/settings")) return "계정, 권한, 재고 위치 같은 내부 운영 설정을 관리합니다.";
  return "오늘 처리할 업무와 예외를 확인합니다.";
}
